import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'productChecked',
    'returnType',
    'quantity',
    'submitButton',
    'freight',
    'cancelationReason',
    'productInfo',
    'stocks',
    'customerAddress',
    'customerAddressCount',
    'customer',
    'customerWithDocument',
    'cancelationReasonWarning',
    'cancelationReasonField',
    'cancelationReasonRequired',
  ]

  // Motivos incompativeis com cada tipo de devolucao, por chave (`reason`).
  // O tipo considerado e o EFETIVO - somatorio das quantidades vs. maximo devolvivel -,
  // que e o mesmo criterio do servidor (Orders::DevolveService#full_devolve?). O select
  // 'Tipo de devolucao' nao decide nada no backend: escolher 'total' apenas leva as
  // quantidades ao maximo, e o filtro reage a isso.
  static blockedReasons = {
    total: ['partial_refusal'],
    partial: ['total_refusal', 'failure_to_locate_recipient'],
  }

  connect() {
    const that = this

    if (this.element.dataset.deliveryProblem) {
      this.handleTotalDevolution()
    }

    this.normalizeQuantities()
    this.filterCancelationReasons()
    this.toggleSubmitButton()
    const listener = function(e) {
      const documentPresent = e.detail.documentPresent
      const count = e.detail.addressCount

      that.customerAddressCountTarget.value = count
      that.customerWithDocumentTarget.value = documentPresent

      that.toggleSubmitButton()
    }

    document.removeEventListener('addressesLoaded', listener)
    document.addEventListener('addressesLoaded', listener)
  }

  handleChange(event) {
    if (event.target.dataset.target == 'products-return.returnType') this.handleTotalDevolution()

    this.filterCancelationReasons()
    this.toggleSubmitButton()
  }

  handleQuantityChange(event) {
    this.normalizeQuantity(event.target)
    this.filterCancelationReasons()
    this.toggleSubmitButton()
  }

  validateSubmit(event) {
    this.normalizeQuantities()
    this.toggleSubmitButton()

    if (this.formValid()) return

    event.preventDefault()
    event.stopImmediatePropagation()

    // Presenca do motivo: reportValidity() nao serve aqui porque o Semantic UI esconde o
    // select e o browser nao reporta validade de campo invisivel.
    this.toggleCancelationReasonRequired(!this.hasCancelationReason())

    const invalidInput = this.invalidQuantityInput()
    if (invalidInput) invalidInput.reportValidity()
  }

  toggleSubmitButton() {
    const invalid = !this.formValid()

    this.submitButtonTarget.classList.toggle('is-disabled', invalid)
    this.submitButtonTarget.disabled = invalid
  }

  formValid() {
    return (
      this.freightTarget.value != '' &&
      this.hasCancelationReason() &&
      this.hasProductChecked() &&
      this.quantitiesValid() &&
      this.customerInfoFilled()
    )
  }

  customerInfoFilled() {
    // Pedido sem cliente: o campo fica ativo para o operador escolher um, e ate a escolha os dois
    // hidden abaixo estao vazios - nao '0' nem 'false' -, entao eles nao barram nada e o operador
    // conseguiria enviar sem cliente para o DevolveService recusar. Com cliente no pedido o select
    // e inativo e ja vem preenchido, entao esta linha nao muda nada nesse caso.
    if (this.customerTarget.value === '') return false
    if (this.customerAddressCountTarget.value === '0') return false
    if (this.customerWithDocumentTarget.value === false || this.customerWithDocumentTarget.value === 'false')
      return false

    return true
  }

  handleTotalDevolution() {
    if (this.returnTypeTarget.value == 'total') {
      this.productInfoTargets.forEach(ele => ele.classList.add('is-disabled'))

      this.quantityTargets.forEach(input => {
        input.value = input.max
        input.classList.add('is-disabled')
        this.setQuantityValidity(input)
      })

      this.productCheckedTargets.forEach((input, index) => {
        if (this.quantityValue(this.quantityTargets[index]) <= 0) {
          input.checked = false
        } else {
          input.checked = true
        }
        input.classList.add('is-disabled')
      })
    } else {
      this.productInfoTargets.forEach(ele => ele.classList.remove('is-disabled'))

      this.quantityTargets.forEach(input => {
        input.value = 0
        input.classList.remove('is-disabled')
        this.setQuantityValidity(input)
      })

      this.productCheckedTargets.forEach(input => {
        input.checked = false
        input.classList.remove('is-disabled')
      })
    }
  }

  hasProductChecked() {
    if (this.returnTypeTarget.value == 'total') {
      let availableProductCheckedTarget = []
      let availableProductQuantityTarget = []
      for (let index = 0; index < this.productCheckedTargets.length; index++) {
        if (this.maxQuantityFor(this.quantityTargets[index]) !== 0) {
          availableProductCheckedTarget.push(this.productCheckedTargets[index])
          availableProductQuantityTarget.push(this.quantityTargets[index])
        }
      }

      return (
        availableProductCheckedTarget.every(input => input.checked) &&
        availableProductQuantityTarget.every(input => this.quantityValue(input) === this.maxQuantityFor(input))
      )
    } else {
      return this.productCheckedTargets.some((input, index) => input.checked && this.quantityValue(this.quantityTargets[index]) > 0)
    }
  }

  // Tipo efetivo da devolucao, pelo mesmo criterio do servidor: tudo que da para devolver
  // esta sendo devolvido.
  effectiveReturnType() {
    const returned = this.quantityTargets.reduce((sum, input) => sum + this.quantityValue(input), 0)
    const returnable = this.quantityTargets.reduce((sum, input) => sum + this.maxQuantityFor(input), 0)

    if (returnable <= 0 || returned < returnable) return 'partial'

    return 'total'
  }

  filterCancelationReasons() {
    if (!this.hasCancelationReasonTarget) return

    const select = this.cancelationReasonTarget
    const blocked = this.constructor.blockedReasons[this.effectiveReturnType()] || []
    let clearedSelection = false

    Array.from(select.options).forEach(option => {
      const isBlocked = option.dataset.reason && blocked.includes(option.dataset.reason)

      option.hidden = isBlocked
      option.disabled = isBlocked
      this.toggleDropdownItem(option.value, isBlocked)

      if (isBlocked && select.value == option.value) clearedSelection = true
    })

    if (clearedSelection) this.clearCancelationReason()

    this.toggleCancelationReasonWarning(clearedSelection)

    // Escolheu um motivo valido e depois levou tudo ao maximo: a selecao foi limpa, entao a
    // pendencia de preenchimento passa a valer imediatamente, sem esperar a tentativa de
    // submit - e o caso que motivou a validacao de presenca.
    if (clearedSelection) this.toggleCancelationReasonRequired(true)
    if (this.hasCancelationReason()) this.toggleCancelationReasonRequired(false)
  }

  // O select virou um menu do Semantic UI (o $('.ui.dropdown').dropdown() no fim da view
  // orders/order_returns/new.html.erb): esconder a <option> nao tira o item da lista que o
  // usuario ve. A busca e escopada no wrapper do campo porque o formulario tem varios
  // dropdowns, cada um com seu .menu.
  toggleDropdownItem(value, hidden) {
    if (!this.hasCancelationReasonFieldTarget) return

    const item = this.cancelationReasonFieldTarget.querySelector(`.menu .item[data-value="${value}"]`)
    if (item) item.hidden = hidden
  }

  clearCancelationReason() {
    const select = this.cancelationReasonTarget

    select.value = ''

    // dropdown('clear') zera tambem o rotulo visivel do Semantic; sem isso o usuario
    // continuaria vendo o motivo antigo escrito no campo.
    if (window.jQuery) window.jQuery(select).dropdown('clear')
  }

  toggleCancelationReasonWarning(visible) {
    if (!this.hasCancelationReasonWarningTarget) return

    this.cancelationReasonWarningTarget.hidden = !visible
  }

  toggleCancelationReasonRequired(visible) {
    if (!this.hasCancelationReasonRequiredTarget) return

    this.cancelationReasonRequiredTarget.hidden = !visible
  }

  hasCancelationReason() {
    const cancelReasonEmpty = this.cancelationReasonTarget.value == ''

    if (cancelReasonEmpty) return false

    return true
  }

  quantitiesValid() {
    return this.quantityTargets.every(input => {
      const value = this.quantityValue(input)
      const min = this.minQuantityFor(input)
      const max = this.maxQuantityFor(input)

      return value >= min && value <= max
    })
  }

  invalidQuantityInput() {
    return this.quantityTargets.find(input => !input.validity.valid)
  }

  normalizeQuantities() {
    this.quantityTargets.forEach(input => this.normalizeQuantity(input))
  }

  normalizeQuantity(input) {
    const min = this.minQuantityFor(input)
    const max = this.maxQuantityFor(input)
    const value = this.quantityValue(input)

    let normalized = value
    if (Number.isNaN(normalized)) normalized = min

    normalized = Math.min(Math.max(normalized, min), max)
    input.value = normalized

    this.setQuantityValidity(input)
  }

  setQuantityValidity(input) {
    input.setCustomValidity('')
  }

  quantityValue(input) {
    return Number(input.value)
  }

  minQuantityFor(input) {
    return Number(input.min || 0)
  }

  maxQuantityFor(input) {
    return Number(input.max || 0)
  }
}
